import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import {
  Box,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import PropTypes from "prop-types";
import { useMemo } from "react";

import { IRIA_PORTAL_STAGE_STEPS } from "../projectWorkspace/loanMehrsanStage/loanMehrsanStageConstants";

const EXTERNAL_STATUS_ORDER = {
  draft: 1,
  inquiry_pending: 2,
  inquiry_done: 3,
  contract_requested: 4,
  contract_downloaded: 5,
  signed_uploaded: 6,
  send_to_bank_pending: 7,
  pay_pending: 7,
  pay_failed: 7,
  pay_done: 8,
  completed: 9,
};

const LEGACY_STAGE_ORDER = {
  IriaSubmitApplicant: 1,
  IriaSubmitRequest: 1,
  IriaCreditCheck: 2,
  IriaInquiryPending: 2,
  IriaDocumentCollect: 3,
  IriaInquiryDone: 3,
  IriaContractSign: 4,
  IriaContractRequested: 4,
  IriaContractDownloaded: 5,
  IriaSignedUploaded: 6,
  IriaBankPending: 7,
  IriaDisburse: 8,
  IriaCompleted: 9,
};

export const resolveIriaPortalActiveOrder = ({
  loanSteps = [],
  externalStatusCode = "",
  currentStageCode = "",
} = {}) => {
  const normalizedStatus = String(externalStatusCode || "")
    .trim()
    .toLowerCase();
  if (normalizedStatus && EXTERNAL_STATUS_ORDER[normalizedStatus]) {
    return EXTERNAL_STATUS_ORDER[normalizedStatus];
  }

  const normalizedStage = String(currentStageCode || "").trim();
  if (normalizedStage && LEGACY_STAGE_ORDER[normalizedStage]) {
    return LEGACY_STAGE_ORDER[normalizedStage];
  }

  for (const def of IRIA_PORTAL_STAGE_STEPS) {
    const step = loanSteps.find(
      (row) => row.stageCode === def.stageCode || row.id === def.id
    );
    if (step?.status !== "completed") {
      return def.id;
    }
  }

  return IRIA_PORTAL_STAGE_STEPS.length;
};

export const buildIriaPortalTimelineItems = (options) => {
  const activeOrder = resolveIriaPortalActiveOrder(options);
  const { loanSteps = [] } = options || {};

  return IRIA_PORTAL_STAGE_STEPS.map((def) => {
    const step = loanSteps.find(
      (row) => row.stageCode === def.stageCode || row.id === def.id
    );
    const completed = step?.status === "completed" || def.id < activeOrder;
    const active = def.id === activeOrder && !completed;

    return {
      ...def,
      completed,
      active,
      pending: !completed && !active,
    };
  });
};

const IriaPortalStagesTimeline = ({
  loanSteps = [],
  externalStatusCode = "",
  currentStageCode = "",
  compact = false,
}) => {
  const items = useMemo(
    () =>
      buildIriaPortalTimelineItems({
        loanSteps,
        externalStatusCode,
        currentStageCode,
      }),
    [loanSteps, externalStatusCode, currentStageCode]
  );

  return (
    <Paper variant="outlined" sx={{ p: compact ? 1.5 : 2, borderRadius: 2 }}>
      <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5 }}>
        مراحل فرآیند (پنل ایریاپی)
      </Typography>
      <Stack spacing={0} sx={{ position: "relative", pr: 1.5 }}>
        {items.map((item, index) => (
          <Stack
            key={item.stageCode}
            direction="row"
            spacing={1.5}
            sx={{
              position: "relative",
              pb: index < items.length - 1 ? 1.5 : 0,
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                width: 24,
                flexShrink: 0,
              }}
            >
              {item.completed ? (
                <CheckCircleIcon sx={{ fontSize: 20, color: "success.main" }} />
              ) : item.active ? (
                <FiberManualRecordIcon sx={{ fontSize: 20, color: "primary.main" }} />
              ) : (
                <FiberManualRecordIcon sx={{ fontSize: 16, color: "grey.400", mt: 0.25 }} />
              )}
              {index < items.length - 1 ? (
                <Box
                  sx={{
                    width: 2,
                    flex: 1,
                    minHeight: 16,
                    bgcolor: item.completed ? "success.light" : "grey.300",
                    mt: 0.5,
                  }}
                />
              ) : null}
            </Box>
            <Box sx={{ flex: 1, minWidth: 0, pt: 0.1 }}>
              <Stack direction="row" flexWrap="wrap" gap={0.75} alignItems="center">
                <Typography
                  variant="body2"
                  fontWeight={item.active ? 700 : 600}
                  color={item.pending ? "text.secondary" : "text.primary"}
                >
                  {item.title}
                </Typography>
                {item.active ? (
                  <Typography variant="caption" color="primary.main" fontWeight={700}>
                    مرحله جاری
                  </Typography>
                ) : null}
              </Stack>
              {item.actorLabel ? (
                <Typography variant="caption" color="text.secondary">
                  {item.actorLabel}
                </Typography>
              ) : null}
            </Box>
          </Stack>
        ))}
      </Stack>
    </Paper>
  );
};

IriaPortalStagesTimeline.propTypes = {
  loanSteps: PropTypes.array,
  externalStatusCode: PropTypes.string,
  currentStageCode: PropTypes.string,
  compact: PropTypes.bool,
};

export default IriaPortalStagesTimeline;
